/**
 * State provider keeps state of buyerID, selected filters, search and pagination (all query inputs)
 *
 * This goes when the inputs market is removed. The quote components that read its buyerID will
 * take it from useMarketStateStore, which already holds the same state for the fresh market, so
 * the provider goes whole rather than being split up now.
 */
import { createContext, ReactNode, useContext, useMemo, useState, useCallback } from 'react';
import useDebouncedFunc from 'hooks/useDebouncedFunc';
import useProfileConfig from 'hooks/useProfileConfig';
import { DEFAULT_PAGE_SIZE } from 'utils/constants';
import { DEFAULT_PAGINATION_OPTION } from 'utils/market';
import { FilterInput } from 'generated/graphql';
import { PaginationOption, RefetchType } from 'generated/graphql';

interface InputsStateProviderContextProps {
  queryInput: {
    search: string;
    filters: FilterInput[];
    buyerID: string;
    paginationOption: PaginationOption;
  };
  searchState: {
    searchValue: string;
    setSearch: (newSearch: string) => void;
    resetSearch: () => void;
  };
  filtersState: {
    selectedFilters: FilterInput[];
    setSelectedFilters: (filters: FilterInput[]) => void;
  };
  paginationState: {
    onReset: () => void;
    onNext: () => void;
    onPaginate: (pageNumber: number, ids?: string[], refetchType?: RefetchType) => void;
  };
  buyerState: {
    buyerID: string | undefined;
    setBuyerID: (newBuyerID: string) => void;
  };
}

export const Context = createContext<InputsStateProviderContextProps | undefined>(undefined);

export const InputsStateProvider = ({ children }: { children: ReactNode }) => {
  const [selectedFilters, setSelectedFilters] = useState<FilterInput[]>([]); // Note from buyermarket: Used for filtering none visual logic. FilterInput[] used for marketStateStore.setInput
  const [paginationOption, setPaginationOption] = useState<PaginationOption>(DEFAULT_PAGINATION_OPTION);
  const [searchValue, setSearchValue] = useState<string>('');
  const { profileID } = useProfileConfig();
  const [buyerID, setBuyerID] = useState<string | undefined>(profileID);

  /** Pagination */
  const handlePaginate = useCallback((pageNumber: number, ids: string[] = [], refetchType = RefetchType.Unknown) => {
    setPaginationOption({
      pageNumber,
      pageSize: DEFAULT_PAGE_SIZE,
      ids: ids,
      type: refetchType,
    });
  }, []);

  const handleResetPagination = useCallback(() => {
    setPaginationOption(DEFAULT_PAGINATION_OPTION);
  }, []);

  const debouncedSearch = useDebouncedFunc((value: string) => {
    setSearchValue(value);
  }, 800);

  const handleSearchChange = useCallback(
    (value: string) => {
      if (value !== searchValue) {
        handleResetPagination();
        debouncedSearch(value);
      }
    },
    [searchValue, handleResetPagination, debouncedSearch],
  );

  const value = useMemo(
    () => ({
      queryInput: {
        search: searchValue,
        filters: selectedFilters,
        buyerID: buyerID || '',
        paginationOption: paginationOption,
      },
      searchState: {
        searchValue,
        setSearch: handleSearchChange,
        resetSearch: () => handleSearchChange(''),
      },
      buyerState: {
        buyerID,
        setBuyerID: (newBuyerID: string) => {
          setBuyerID(newBuyerID);
          handleResetPagination();
        },
      },
      filtersState: {
        selectedFilters,
        setSelectedFilters: (filters: FilterInput[]) => {
          setSelectedFilters(filters);
          handleResetPagination();
        },
      },
      paginationState: {
        onReset: () => handlePaginate(1),
        onNext: () => handlePaginate((paginationOption.pageNumber ?? 1) + 1),
        onPaginate: handlePaginate,
      },
    }),
    [
      searchValue,
      selectedFilters,
      buyerID,
      paginationOption,
      handleSearchChange,
      handlePaginate,
      handleResetPagination,
    ],
  );

  return <Context.Provider value={value}>{children}</Context.Provider>;
};

export const useInputsState = () => {
  const result = useContext(Context);
  if (!result) throw new Error('useInputsState must be used within an InputsStateProvider');
  return result;
};

export const useInputsStateSafe = () => {
  return useContext(Context);
};
